/**
 * Following somebody through a book.
 *
 * The decisions live here as pure functions, and the behavior that uses them
 * lives in `useFollowing`. That split is `useListbox`'s: what a keystroke or a
 * frame DOES follows from its arguments alone, so it can be tested directly —
 * and whether a reader who asked for less motion got less of it is invisible in
 * a rendered page and shows up only as the page traveling when it should not.
 *
 * Nothing here touches the DOM or `matchMedia`. The platform reads happen at
 * the edge and arrive as arguments.
 */

/**
 * Where the block being followed comes to rest, as a fraction of the window.
 *
 * Above center, because a reader reads ON from the line they are given: resting
 * the leader's passage at the very top would put everything they are about to
 * read off the bottom of the screen, and resting it in the middle wastes the
 * half above it. The chord sheet's own follow uses the same third for the same
 * reason.
 *
 * It is a sightline, not a mark: nothing is drawn there.
 */
export const SIGHTLINE = 0.34

/** How much of the remaining distance is closed each frame — a follow, not a jump. */
const EASE = 0.12

/** Closer than this and the page is already where it should be. */
const SETTLED = 0.5

/**
 * How far the page may sit from where we left it before the movement is
 * somebody else's.
 *
 * Two pixels, to absorb fractional device-pixel rounding. Told apart by
 * POSITION rather than by a flag, because a scroll event arrives after the fact
 * and carries no attribution — a flag cleared too early swallows a real scroll,
 * and cleared too late eats the interrupt it was meant to catch.
 */
const NOT_OURS = 2

/** How far a finger travels before it is moving the page rather than pressing it. */
const TRAVEL = 8

/**
 * How long the page must be still before a reader says where they are.
 *
 * A reader moving continuously should produce a handful of positions, not a
 * stream of them: every one is a write, and nobody following needs to be told
 * about a passage that went by. Long enough that a flick through several
 * screens publishes once at the end, short enough that stopping to read is
 * reported before the person following gives up on you.
 */
export const SETTLE_MS = 400

/**
 * How far to move the page this frame, given how far off it is.
 *
 * **Reduced motion is answered with a different motion, never with none.** A
 * reader who asked for less movement still asked to follow somebody, so they
 * are put where they need to be rather than glided there — the whole distance
 * at once, which is a movement that does not travel. The app's blanket
 * reduced-motion reset cannot reach this: it shortens transitions and
 * animations, and a scroll is neither.
 *
 * Zero where the page is close enough, which is what stops sub-pixel jitter
 * from re-arming the drift check on every frame.
 */
export function stepToward(gap: number, reduced: boolean): number {
  const by = reduced ? gap : gap * EASE
  return Math.abs(by) > SETTLED ? by : 0
}

/** How far the top of the followed block is from where it should rest. */
export function gapToSightline(top: number, viewportHeight: number): number {
  return top - viewportHeight * SIGHTLINE
}

/**
 * Whether the page moved because somebody moved it.
 *
 * Compared against where the follow LEFT it rather than against a flag. A
 * scroll event says only that the page moved, never who moved it.
 */
export function movedByHand(scrollY: number, expected: number): boolean {
  return Math.abs(scrollY - expected) > NOT_OURS
}

/**
 * Whether a touch has become a drag.
 *
 * A touch is not yet a move. A book is read on a phone where the roster, the
 * companion opener and the reading are all under one thumb, and ending the
 * follow the moment a finger lands would make every control a way to stop
 * following by accident. Measured from where the finger landed rather than
 * per-event, so it is total travel.
 */
export function isDrag(from: number | null, to: number | null): boolean {
  if (from === null || to === null) return false
  return Math.abs(to - from) > TRAVEL
}

/** Why following ended, so the reader can be told rather than left guessing. */
export type FollowEnd =
  /** They closed the book. */
  | 'gone'
  /** Their passage is not in this copy — the book changed under one of us. */
  | 'lost'
  /** You scrolled. */
  | 'byHand'

/**
 * What to say about a reader being followed, given what the room reports.
 *
 * `null` means carry on. The two failures are told apart deliberately: a leader
 * who left and a passage that does not resolve look identical to a follower
 * whose page simply stopped moving, and `glideToElement` returns silently on a
 * miss — so without this the reader cannot tell being finished from being
 * broken.
 */
export function whyStopped(
  who: { at: string | null } | undefined,
  resolves: boolean,
): FollowEnd | null {
  if (!who) return 'gone'
  if (who.at !== null && !resolves) return 'lost'
  return null
}
